import { GoTrueClient } from '../lib/gotrueClient'

const SUPABASE_URL_KEY = 'dreamwright_supabase_url'
const SUPABASE_ANON_KEY_KEY = 'dreamwright_supabase_anon_key'

// Optional override for the GoTrue (auth) base URL. When set, supabase-js
// sends auth-flow requests (signup, token, recovery, etc.) directly to this
// URL instead of constructing them from `${SUPABASE_URL}/auth/v1`. Used to
// point the console at a self-hosted GoTrue Cloud Run service before a
// custom-domain LB exists in front of it (RFC-0629 Stage 0 dev rehearsal
// workaround). When empty, the standard `${SUPABASE_URL}/auth/v1` path
// applies — no behavior change.
const GOTRUE_URL_KEY = 'dreamwright_gotrue_url'

type AuthStorageBackend = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> &
  Partial<Pick<Storage, 'length' | 'key'>>
type AuthStorageResolver = () => AuthStorageBackend | null

const authMemoryStorage = new Map<string, string>()

const DISPOSABLE_LOCAL_STORAGE_KEYS = [
  'chat-sidebar-collapsed',
  'dreamwright_auto_confirm_image_costs',
  'dreamwright_auto_confirm_video_costs',
  'dreamwright_canvas_performance_settings_v2',
  'landing-aspect-ratio',
  'landing-output-mode',
  'videoEditor:panel-sizes',
  'dw:timeline:header-column-px',
]

const DISPOSABLE_LOCAL_STORAGE_PREFIXES = [
  'dw:canvas-regen-cue:',
]

// Per-environment defaults for the Supabase API URL. Selected by Vite's
// build MODE — `production` for `vite build` (deploy-production workflow),
// `staging` for `vite build --mode staging` (deploy-staging workflow),
// `development` for `vite dev` (local) and `vite build --mode development`
// (deploy-dev workflow). Points at the auth subdomain served by the api
// LB (gce_api.tf path_matcher "auth"), which strips /auth/v1/ and forwards
// to GoTrue. supabase-js constructs requests as `${SUPABASE_URL}/auth/v1/*`
// so the URL shape lines up with what the LB expects (RFC-0670 AD-1).
//
// Data calls (.from()/.rpc()/.storage) are no longer issued from the
// console; the only live surface here is supabase-js auth.
//
// VITE_SUPABASE_URL at build time overrides the mode-derived default for
// one-off builds without code changes.
const SUPABASE_URLS_BY_MODE: Record<string, string> = {
  production: 'https://auth.dream-wright.com',
  staging: 'https://auth.staging.dreamwrite.com',
  development: 'https://auth.dev.dreamwrite.com',
}

function defaultSupabaseUrlForMode(): string {
  try {
    const explicit = import.meta.env.VITE_SUPABASE_URL?.trim()
    if (explicit) return explicit
    return SUPABASE_URLS_BY_MODE[import.meta.env.MODE] ?? SUPABASE_URLS_BY_MODE.development
  } catch {
    // SSR / test contexts where import.meta.env is unavailable.
    return SUPABASE_URLS_BY_MODE.development
  }
}

const DEFAULT_SUPABASE_URL = defaultSupabaseUrlForMode()
// TODO(RFC-0629): the anon JWT below is hosted-Supabase-signed and will fail
// verification against the self-hosted GoTrue secret. Replace with per-env
// anon JWTs minted from each environment's dreamwright-gotrue-jwt-secret
// (runbook step 11). Until then, the user must paste per-env anon JWTs into
// the Settings UI (localStorage) for the new URL to actually authenticate.
const DEFAULT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndieXl2ZXd4a2hpdXdhdmZxb2dpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU3NzE5MzUsImV4cCI6MjA4MTM0NzkzNX0.Ilez7mTjn9o0mE3vZhGOoL1zP_HS-UsgCEJj9wIDGTE'

// RFC-0669 (local-gcp-stack): the local prod-parity dev stack mints a local
// anon JWT signed with the local GoTrue secret and injects it at build time as
// VITE_SUPABASE_ANON_KEY (via scripts/_dev-nad.sh → tmp/local-gcp-stack/
// .env.console). The committed DEFAULT_SUPABASE_ANON_KEY is hosted-signed and
// would silently fail against the local GoTrue secret, so for LOCAL dev we
// prefer the injected key and fail loudly if it is missing rather than
// half-working. The deployed dev/prod fallback (DEFAULT_SUPABASE_ANON_KEY) is
// left untouched.
function injectedAnonKey(): string | undefined {
  try {
    return import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || undefined
  } catch {
    return undefined
  }
}

function isDevMode(): boolean {
  try {
    return import.meta.env.MODE === 'development'
  } catch {
    return false
  }
}

function isLocalRouterUrl(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(url)
}

function defaultAuthStorageBackend(): AuthStorageBackend | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function isQuotaExceededError(error: Error): boolean {
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.message.toLowerCase().includes('quota')
  )
}

function pruneDisposableLocalStorage(storage: AuthStorageBackend): number {
  let removed = 0
  for (const key of DISPOSABLE_LOCAL_STORAGE_KEYS) {
    try {
      if (storage.getItem(key) !== null) {
        storage.removeItem(key)
        removed += 1
      }
    } catch {
      // Continue pruning best-effort.
    }
  }

  let keyCount = 0
  try {
    keyCount = typeof storage.length === 'number' ? storage.length : 0
  } catch {
    return removed
  }

  for (let index = keyCount - 1; index >= 0; index -= 1) {
    let key: string | null = null
    try {
      key = storage.key?.(index) ?? null
    } catch {
      continue
    }
    if (!key) continue
    if (!DISPOSABLE_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      continue
    }
    try {
      storage.removeItem(key)
      removed += 1
    } catch {
      // Continue pruning best-effort.
    }
  }
  return removed
}

function warnAuthStorageFallback(key: string, error: Error): void {
  if (typeof console === 'undefined') return
  console.warn(
    `[auth] Falling back to in-memory auth storage for ${key}; ` +
      'localStorage is full or unavailable. The session may not survive reload.',
    error,
  )
}

function createQuotaRecoveringAuthStorage(
  resolveStorage: AuthStorageResolver = defaultAuthStorageBackend,
): AuthStorageBackend {
  return {
    getItem(key: string) {
      const storage = resolveStorage()
      if (storage) {
        try {
          return storage.getItem(key) ?? authMemoryStorage.get(key) ?? null
        } catch {
          return authMemoryStorage.get(key) ?? null
        }
      }
      return authMemoryStorage.get(key) ?? null
    },
    setItem(key: string, value: string) {
      const storage = resolveStorage()
      if (!storage) {
        authMemoryStorage.set(key, value)
        return
      }

      try {
        storage.setItem(key, value)
        authMemoryStorage.delete(key)
        return
      } catch (firstStorageError) {
        const firstError = firstStorageError instanceof Error
          ? firstStorageError
          : new Error(String(firstStorageError))
        if (isQuotaExceededError(firstError)) {
          pruneDisposableLocalStorage(storage)
          try {
            storage.setItem(key, value)
            authMemoryStorage.delete(key)
            return
          } catch (secondStorageError) {
            const secondError = secondStorageError instanceof Error
              ? secondStorageError
              : new Error(String(secondStorageError))
            authMemoryStorage.set(key, value)
            warnAuthStorageFallback(key, secondError)
            return
          }
        }
        authMemoryStorage.set(key, value)
        warnAuthStorageFallback(key, firstError)
      }
    },
    removeItem(key: string) {
      authMemoryStorage.delete(key)
      const storage = resolveStorage()
      if (!storage) return
      try {
        storage.removeItem(key)
      } catch {
        // Ignore storage errors during sign-out / cleanup.
      }
    },
  }
}

const authStorage = createQuotaRecoveringAuthStorage()

// Safe localStorage access
function getFromStorage(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function setToStorage(key: string, value: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (value) {
      localStorage.setItem(key, value)
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    // Ignore storage errors
  }
}

// Get config from localStorage or use defaults
export function getConfiguredSupabaseUrl(): string {
  return getFromStorage(SUPABASE_URL_KEY) || DEFAULT_SUPABASE_URL
}

export function getConfiguredSupabaseAnonKey(): string {
  // Precedence: localStorage override (debugging) > injected build-time key >
  // committed deployed fallback. A localStorage override always wins so the
  // Settings UI / manual debugging keeps working.
  const stored = getFromStorage(SUPABASE_ANON_KEY_KEY)
  if (stored) return stored

  const injected = injectedAnonKey()
  if (injected) return injected

  // LOCAL dev only: when pointed at a localhost router (the local-gcp-stack
  // data plane) the committed hosted anon key would silently fail signature
  // verification against the local GoTrue secret. Fail loudly so the developer
  // knows to bring up the stack (./scripts/dev-nad.sh) which injects the key.
  if (isDevMode() && isLocalRouterUrl(getConfiguredSupabaseUrl())) {
    throw new Error(
      'VITE_SUPABASE_ANON_KEY is not set but the console is pointed at a local ' +
        'Supabase router. Bring up the local data plane with ./scripts/dev-nad.sh ' +
        '(it mints + injects a local anon JWT), or paste a local anon key in ' +
        'Settings. Refusing to fall back to the committed hosted anon key, which ' +
        'will not validate against the local GoTrue secret (RFC-0669).'
    )
  }

  return DEFAULT_SUPABASE_ANON_KEY
}

export function setSupabaseUrl(url: string): void {
  setToStorage(SUPABASE_URL_KEY, url || null)
}

export function setSupabaseAnonKey(key: string): void {
  setToStorage(SUPABASE_ANON_KEY_KEY, key || null)
}

// Optional GoTrue auth URL override (see GOTRUE_URL_KEY comment at top).
// Falls back to import.meta.env.VITE_GOTRUE_URL for build-time configuration.
export function getConfiguredGotrueUrl(): string {
  const stored = getFromStorage(GOTRUE_URL_KEY)
  if (stored) return stored
  // Vite inlines env vars at build; safe-guarded for SSR / test contexts.
  try {
    return import.meta.env.VITE_GOTRUE_URL || ''
  } catch {
    return ''
  }
}

export function setGotrueUrl(url: string): void {
  setToStorage(GOTRUE_URL_KEY, url || null)
}

export function hasGotrueOverride(): boolean {
  return !!getConfiguredGotrueUrl()
}

export function hasSupabaseConfig(): boolean {
  return !!(getConfiguredSupabaseUrl() && getConfiguredSupabaseAnonKey())
}

// Derive the auth base URL. A configured GoTrue override (RFC-0629 dev
// rehearsal) wins; otherwise auth requests go to the standard
// `${SUPABASE_URL}/auth/v1` path GoTrue is served under. The in-house
// GoTrueClient appends endpoints (`/token`, `/user`, …) directly to this base,
// reproducing both URL shapes the old supabase-js + fetch-rewrite hack produced.
function getAuthBaseUrl(): string {
  const gotrueUrl = getConfiguredGotrueUrl()
  if (gotrueUrl) return gotrueUrl.replace(/\/+$/, '')
  return `${getConfiguredSupabaseUrl().replace(/\/+$/, '')}/auth/v1`
}

// supabase-js persisted the session under `sb-<host-label>-auth-token`, where
// the label is the first dotted segment of the SUPABASE_URL host (NOT the
// GoTrue override). Reproduce that derivation EXACTLY so existing logged-in
// users' sessions stay readable across the SDK swap and the Playwright specs
// that seed this key keep working.
function getAuthStorageKey(): string {
  try {
    const host = new URL(getConfiguredSupabaseUrl()).hostname
    return `sb-${host.split('.')[0]}-auth-token`
  } catch {
    return 'sb-auth-token'
  }
}

function buildGoTrueClient(): GoTrueClient {
  return new GoTrueClient({
    authBaseUrl: getAuthBaseUrl(),
    anonKey: getConfiguredSupabaseAnonKey(),
    storage: authStorage,
    storageKey: getAuthStorageKey(),
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  })
}

// Lazily-constructed singleton so a missing local anon key throws only on first
// auth use (matching the old createClient-in-getter behavior), never at import.
let gotrueClient: GoTrueClient | null = null

function getSupabaseClient(): GoTrueClient {
  if (!gotrueClient) {
    gotrueClient = buildGoTrueClient()
  }
  return gotrueClient
}

export function reinitializeSupabase(): void {
  // Dispose the old client first: unlike a GC'd supabase-js instance, the
  // in-house client owns a refresh timer + a `storage` event listener that
  // must be torn down or they leak / double-fire after a config change.
  if (gotrueClient) gotrueClient.dispose()
  gotrueClient = buildGoTrueClient()
}

// The `supabase.auth.*` surface the console's auth consumers use, now backed by
// the in-house GoTrueClient (RFC-0889 Sprint 4) instead of the Supabase JS SDK.
// The data proxies (.from/.storage/.rpc/.functions/.realtime) were removed:
// RFC-0670 deleted all frontend data calls, leaving them with zero live callers.
export const supabase = {
  get auth() {
    return getSupabaseClient()
  },
}

export const __testing = {
  createQuotaRecoveringAuthStorage,
  isQuotaExceededError,
}
